import { buildWaiter } from '@ember/test-waiters';

const waiter = buildWaiter('spds-tooltip-registration');

/**
 * Keeps one tooltip source registered with the `spds/tooltip` service only while it has content.
 * A call made while an earlier one is pending is chained behind it, so calls reach the container in
 * issue order: `register` waits for the container's channel but `unregister` doesn't, so an
 * unchained unregister could land first and leave an orphaned tooltip. A call with nothing pending
 * runs immediately, keeping its order relative to tooltips registered on the service directly.
 *
 * Pass the service in rather than looking it up later, since `teardown` can run after the owner is
 * destroyed.
 */
export default class TooltipRegistration {
  #id;
  #pending = 0;
  #queue = null;
  #registered = false;
  #tooltip;

  constructor(tooltip, id) {
    this.#tooltip = tooltip;
    this.#id = id;
  }

  sync(config) {
    if (!config.content) return this.teardown();

    let method = this.#registered ? 'update' : 'register';

    this.#registered = true;
    this.#enqueue(() => this.#tooltip[method](this.#id, config));
  }

  teardown() {
    if (!this.#registered) return;

    this.#registered = false;
    this.#enqueue(() => this.#tooltip.unregister(this.#id));
  }

  #enqueue(op) {
    let previous = this.#pending ? this.#queue : null;

    this.#pending++;
    this.#queue = this.#run(previous, op);
  }

  async #run(previous, op) {
    let token = waiter.beginAsync();

    try {
      if (previous) await previous;
    } catch {
      // Already surfaced by the call that rejected; don't block the ones after it.
    }

    try {
      return await op();
    } finally {
      this.#pending--;
      waiter.endAsync(token);
    }
  }
}
